import { getStoredToken } from "./api";
import { createApiError } from "../utils/errorHandling";
import { API_BASE_URL } from "../config/api";

// ─── Types ────────────────────────────────────────────────────────────

export interface CompanyContextData {
  id?: number;
  company_id?: number;
  legal_name?: string;
  trading_name?: string;
  company_type?: string;
  website?: string;
  core_business?: string;
  revenue_streams?: string;
  expense_categories?: string;
  raw_input?: string;
  ai_rewritten_context?: string;
  final_context?: string;
  status?: "draft" | "generated" | "confirmed";
}

export interface VendorContextData {
  id?: number;
  vendor_id?: number;
  vendor_name?: string;
  default_expense_category?: string;
  gst_applicable?: "Y" | "N";
  gst_rate?: number;
  recurring_billing?: "Y" | "N";
  contract_exists?: "Y" | "N";
  raw_input?: string;
  ai_rewritten_context?: string;
  final_context?: string;
  status?: "draft" | "generated" | "confirmed";
}

export interface CustomerContextData {
  id?: number;
  customer_id?: number;
  customer_name?: string;
  default_revenue_category?: string;
  repeat_business_expected?: "Y" | "N";
  raw_input?: string;
  ai_rewritten_context?: string;
  final_context?: string;
  status?: "draft" | "generated" | "confirmed";
}

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

// ─── Helpers ──────────────────────────────────────────────────────────

const getHeaders = () => {
  const token = getStoredToken();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

const handleError = async (response: Response, fallbackMsg: string) => {
  let errorData;
  try {
    errorData = await response.json();
  } catch {
    errorData = { message: "Failed to parse error response" };
  }
  throw createApiError(
    errorData.message || fallbackMsg,
    response.status,
    response.statusText
  );
};

// ─── Company Context ──────────────────────────────────────────────────

export const getCompanyContext = async (): Promise<
  ApiResponse<CompanyContextData | null>
> => {
  const response = await fetch(`${API_BASE_URL}/api/context/company`, {
    method: "GET",
    headers: getHeaders(),
  });
  if (!response.ok) await handleError(response, "Failed to fetch company context");
  return response.json();
};

export const saveCompanyContext = async (
  data: Partial<CompanyContextData>
): Promise<ApiResponse<CompanyContextData>> => {
  const response = await fetch(`${API_BASE_URL}/api/context/company`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(data),
  });
  if (!response.ok) await handleError(response, "Failed to save company context");
  return response.json();
};

export const generateCompanyContext = async (
  data: Partial<CompanyContextData>
): Promise<ApiResponse<{ ai_rewritten_context: string }>> => {
  const response = await fetch(
    `${API_BASE_URL}/api/context/company/generate`,
    {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(data),
    }
  );
  if (!response.ok)
    await handleError(response, "Failed to generate company context");
  return response.json();
};

export const confirmCompanyContext = async (
  data: { final_context?: string }
): Promise<ApiResponse<CompanyContextData>> => {
  const response = await fetch(
    `${API_BASE_URL}/api/context/company/confirm`,
    {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(data),
    }
  );
  if (!response.ok)
    await handleError(response, "Failed to confirm company context");
  return response.json();
};

// ─── Vendor Context ───────────────────────────────────────────────────

export const getVendorContext = async (
  vendorId: number
): Promise<ApiResponse<VendorContextData | null>> => {
  const response = await fetch(
    `${API_BASE_URL}/api/context/vendor/${vendorId}`,
    {
      method: "GET",
      headers: getHeaders(),
    }
  );
  if (!response.ok) await handleError(response, "Failed to fetch vendor context");
  return response.json();
};

export const saveVendorContext = async (
  vendorId: number,
  data: Partial<VendorContextData>
): Promise<ApiResponse<VendorContextData>> => {
  const response = await fetch(
    `${API_BASE_URL}/api/context/vendor/${vendorId}`,
    {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(data),
    }
  );
  if (!response.ok) await handleError(response, "Failed to save vendor context");
  return response.json();
};

export const generateVendorContext = async (
  data: Partial<VendorContextData>
): Promise<ApiResponse<{ ai_rewritten_context: string }>> => {
  const response = await fetch(
    `${API_BASE_URL}/api/context/vendor/generate`,
    {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(data),
    }
  );
  if (!response.ok)
    await handleError(response, "Failed to generate vendor context");
  return response.json();
};

// ─── Customer Context ─────────────────────────────────────────────────

export const getCustomerContext = async (
  customerId: number
): Promise<ApiResponse<CustomerContextData | null>> => {
  const response = await fetch(
    `${API_BASE_URL}/api/context/customer/${customerId}`,
    {
      method: "GET",
      headers: getHeaders(),
    }
  );
  if (!response.ok)
    await handleError(response, "Failed to fetch customer context");
  return response.json();
};

export const saveCustomerContext = async (
  customerId: number,
  data: Partial<CustomerContextData>
): Promise<ApiResponse<CustomerContextData>> => {
  const response = await fetch(
    `${API_BASE_URL}/api/context/customer/${customerId}`,
    {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(data),
    }
  );
  if (!response.ok)
    await handleError(response, "Failed to save customer context");
  return response.json();
};

export const generateCustomerContext = async (
  data: Partial<CustomerContextData>
): Promise<ApiResponse<{ ai_rewritten_context: string }>> => {
  const response = await fetch(
    `${API_BASE_URL}/api/context/customer/generate`,
    {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(data),
    }
  );
  if (!response.ok)
    await handleError(response, "Failed to generate customer context");
  return response.json();
};
